import { ViewPlugin, Decoration, EditorView } from '@codemirror/view';
import { getCommentLineRanges, overlapsCommentRange } from './commentUtils.js';

/**
 * Validates that position bounds are within document range
 * @param {number} start - Start position
 * @param {number} end - End position
 * @param {number} docLength - Document length
 * @returns {boolean} true if positions are valid
 */
function isValidPosition(start, end, docLength) {
    return start !== null && 
           end !== null && 
           start >= 0 && 
           end <= docLength && 
           start < end;
}

// Mark decoration for highlighted terms
const termHighlightMark = Decoration.mark({
    class: "cm-highlighted-term"
});


// Theme for term highlighting
const termStyles = EditorView.baseTheme({
    ".cm-highlighted-term": {
        backgroundColor: "var(--color-term-highlight-bg)",
        borderBottom: "2px solid var(--color-term-highlight-border)",
        borderRadius: "2px",
        transition: "background-color 0.2s ease"
    },
    ".cm-highlighted-term:hover": {
        backgroundColor: "var(--color-term-highlight-bg-hover)"
    }
});

// Create the term highlighter plugin
function createTermHighlighter(termsStore) {
    let currentTerms = { 
        selectedEntries: new Set(), 
        termEntries: new Map(),
        claimsModifiedSinceHeavyRefresh: false
    };
    
    return ViewPlugin.fromClass(class {
        constructor(view) {
            this.view = view;
            this.decorations = this.computeDecorations(view);
            
            // Subscribe to terms store changes
            this.unsubscribe = termsStore.subscribe(store => {
                currentTerms = {
                    selectedEntries: store.selectedEntries || new Set(),
                    termEntries: store.termEntries || new Map(),
                    claimsModifiedSinceHeavyRefresh: store.claimsModifiedSinceHeavyRefresh || false
                };
                // Schedule the update for the next animation frame
                requestAnimationFrame(() => {
                    if (this.view) {
                        this.decorations = this.computeDecorations(this.view);
                        this.view.update([]);
                    }
                });
            });
        }

        update(update) {
            if (update.docChanged || update.viewportChanged) {
                this.decorations = this.computeDecorations(update.view);
            }
        }

        computeDecorations(view) {
            const { selectedEntries, termEntries, claimsModifiedSinceHeavyRefresh } = currentTerms;
            if (!selectedEntries || selectedEntries.size === 0) return Decoration.none;

            // Don't show highlights when claims have been modified since last heavy refresh
            // This prevents misaligned highlights when positions are stale
            if (claimsModifiedSinceHeavyRefresh) {
                console.log('[CodeMirror] Skipping highlights - claims modified since heavy refresh');
                return Decoration.none;
            }

            const builder = [];
            const text = view.state.doc.toString();
            if (!text) return Decoration.none;
            
            // Get comment ranges to exclude from highlighting
            const commentRanges = getCommentLineRanges(view);

            console.log('[CodeMirror] Selected entries:', Array.from(selectedEntries));

            // For each selected entry, highlight the parent term and its children
            selectedEntries.forEach(indexedKey => {
                const termData = termEntries.get(indexedKey);
                if (!termData) return;
                
                // Skip highlighting if term needs refresh
                if (termData.needs_refresh) {
                    console.log(`[CodeMirror] Skipping highlight for "${termData.parent_term}" - needs refresh`);
                    return;
                }
                
                const term = termData.parent_term;
                
                // Highlight parent term using entry's position
                if (termData.abs_start_char !== undefined && termData.abs_end_char !== undefined) {
                    const docStart = termData.abs_start_char;
                    const docEnd = termData.abs_end_char;
                    
                    console.log(`[CodeMirror] Highlighting parent '${term}' at absolute positions:`, {
                        docPos: [docStart, docEnd],
                        highlightText: text.substring(docStart, docEnd)
                    });
                    
                    // Validate positions are within document bounds and not in comments
                    if (isValidPosition(docStart, docEnd, text.length) && 
                        !overlapsCommentRange(docStart, docEnd, commentRanges)) {
                        builder.push(termHighlightMark.range(docStart, docEnd));
                    } else if (!isValidPosition(docStart, docEnd, text.length)) {
                        console.warn(`[CodeMirror] Invalid position for parent '${term}': [${docStart}, ${docEnd}] (doc length: ${text.length})`);
                    }
                }
                
                // Highlight additional parent term references (other occurrences)
                if (termData.parent_references && termData.parent_references.abs_start_chars && termData.parent_references.abs_end_chars) {
                    for (let i = 0; i < termData.parent_references.abs_start_chars.length; i++) {
                        const docStart = termData.parent_references.abs_start_chars[i];
                        const docEnd = termData.parent_references.abs_end_chars[i];
                        
                        // Validate positions are within document bounds and not in comments
                        if (isValidPosition(docStart, docEnd, text.length) && 
                            !overlapsCommentRange(docStart, docEnd, commentRanges)) {
                            console.log(`[CodeMirror] Highlighting additional parent reference '${term}' at positions:`, {
                                docPos: [docStart, docEnd],
                                highlightText: text.substring(docStart, docEnd)
                            });
                            builder.push(termHighlightMark.range(docStart, docEnd));
                        } else if (docStart !== null || docEnd !== null) {
                            if (!isValidPosition(docStart, docEnd, text.length)) {
                                // Only warn if positions exist but are invalid (not just null)
                                console.warn(`[CodeMirror] Invalid position for parent reference '${term}': [${docStart}, ${docEnd}]`);
                            }
                        }
                    }
                }
                
                // Highlight child terms from this entry
                if (termData.children && typeof termData.children === 'object') {
                    Object.keys(termData.children).forEach(childTerm => {
                        const childData = termData.children[childTerm];
                        if (childData.references && childData.references.abs_start_chars && childData.references.abs_end_chars) {
                            // Highlight child term using absolute positions
                            for (let i = 0; i < childData.references.abs_start_chars.length; i++) {
                                const docStart = childData.references.abs_start_chars[i];
                                const docEnd = childData.references.abs_end_chars[i];
                                
                                // Validate positions are within document bounds and not in comments
                                if (isValidPosition(docStart, docEnd, text.length) && 
                                    !overlapsCommentRange(docStart, docEnd, commentRanges)) {
                                    console.log(`[CodeMirror] Highlighting child '${childTerm}' at positions:`, {
                                        docPos: [docStart, docEnd],
                                        highlightText: text.substring(docStart, docEnd)
                                    });
                                    builder.push(termHighlightMark.range(docStart, docEnd));
                                } else if (docStart !== null || docEnd !== null) {
                                    if (!isValidPosition(docStart, docEnd, text.length)) {
                                        // Only warn if positions exist but are invalid (not just null)
                                        console.warn(`[CodeMirror] Invalid position for child '${childTerm}': [${docStart}, ${docEnd}]`);
                                    }
                                }
                            }
                        } else {
                            // Fallback to regex search for child terms without positions
                            console.warn(`No references found for child '${childTerm}', falling back to regex search`);
                            const termRegex = new RegExp(`\\b${childTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
                            let match;
                            while ((match = termRegex.exec(text)) !== null) {
                                const start = match.index;
                                const end = match.index + match[0].length;
                                // Skip if match is in a comment
                                if (!overlapsCommentRange(start, end, commentRanges)) {
                                    builder.push(termHighlightMark.range(start, end));
                                }
                            }
                        }
                    });
                }
                
                // Fallback to regex search for parent terms without absolute positions
                if (termData.abs_start_char === undefined || termData.abs_end_char === undefined) {
                    console.warn(`No absolute positions for parent '${term}', falling back to regex search`);
                    const termRegex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
                    let match;
                    while ((match = termRegex.exec(text)) !== null) {
                        const start = match.index;
                        const end = match.index + match[0].length;
                        // Skip if match is in a comment
                        if (!overlapsCommentRange(start, end, commentRanges)) {
                            builder.push(termHighlightMark.range(start, end));
                        }
                    }
                }
            });

            // Sort decorations by start position to avoid "Ranges must be added sorted" error
            builder.sort((a, b) => a.from - b.from || a.to - b.to);
            
            return Decoration.set(builder);
        }

        destroy() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }
    }, {
        decorations: v => v.decorations
    });
}

// Export both the plugin creator and the theme
export { createTermHighlighter, termStyles };